// library
import React, { FC } from "react";
import _ from "lodash";
// material
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { Drawer } from "@mui/material";
import { useTreeItem2, UseTreeItem2Parameters } from '@mui/x-tree-view/useTreeItem2';
import {
    TreeItem2Content,
    TreeItem2IconContainer,
    TreeItem2GroupTransition,
    TreeItem2Label,
    TreeItem2Root,
    TreeItem2Checkbox,
} from '@mui/x-tree-view/TreeItem2';
import { TreeItem2Icon } from '@mui/x-tree-view/TreeItem2Icon';
import { TreeItem2DragAndDropOverlay } from '@mui/x-tree-view/TreeItem2DragAndDropOverlay';
import { styled } from '@mui/material/styles';

// type
import { Code } from "../types/code";

const MapIconController: FC<any> = ( props: { pref: Code[], events: Event[], checkedKeys: [], setCheckedKeys: any }) => {
    const { pref, checkedKeys, setCheckedKeys } = props;    

    let prefectureCode = "0";
    let prefecture = "東京都";
    
    const storagePrefecture = localStorage.getItem('prefecture');

    if (storagePrefecture !== null) {
        prefectureCode = storagePrefecture;

        // 県コードから名前を取得
        const filterPref = _.head(_.filter(pref, (pre: Code) => pre.cd === Number(prefectureCode)))?.name;
        if (filterPref) {
            prefecture = filterPref;
        }
    }

    // スタイル
    const slidePropsStyle = {
        color: '#ffffff',
        backgroundColor: '#46ac14',
        width: 'auto',
        height: 'auto',
        maxHeight: '60vh',
        overflow: 'hidden',
        zIndex: '1000',
        marginTop: '70px',
        borderRadius: '5px',
        marginRight: '10px',
    }

    const ALL_EVENTS: TreeViewBaseItem[] = [
        {
            id: prefectureCode,
            label: prefecture,
            children: [
                { 
                    id: `${prefectureCode}-a`, 
                    label: '開 催 中'
                },
                { 
                    id: `${prefectureCode}-b`, 
                    label: '近日開催'
                },
                { 
                    id: `${prefectureCode}-c`, 
                    label: '1ヶ月以内'
                },
                { 
                    id: `${prefectureCode}-d`, 
                    label: '1ヶ月以降' 
                },
            ],
        },
    ];

    const handleSelectedItemsChange = (_event: React.SyntheticEvent, ids: string[],) => {
        setCheckedKeys(determineIdsToSet(ALL_EVENTS, ids, checkedKeys));
    };

    const determineIdsToSet = (items: TreeViewBaseItem[], newIds: string[], currentIds: string[],) => {

        const isDeselectingNode = currentIds.length > newIds.length;
        if (isDeselectingNode) {
            const removed = currentIds.filter((id) => !newIds.includes(id))[0];
            const parentIdsToRemove = getAllParentIds(items, removed);
            const childIdsToRemove = getSelectedIdsAndChildrenIds(items, [removed]);
            const newIdsWithParentsAndChildrenRemoved = newIds.filter(
                (id) => !parentIdsToRemove.includes(id) && !childIdsToRemove.includes(id),
            );
            return newIdsWithParentsAndChildrenRemoved;
        }

        const added = newIds.filter((id) => !currentIds.includes(id))[0];
        const idsToSet = getSelectedIdsAndChildrenIds(items, newIds);
        let parent = getParentNode(items, added);
        while (parent) {
            const childIds = parent.children?.map((node) => node.id) ?? [];
            const allChildrenSelected = childIds.every((id) => idsToSet.includes(id));
            if (allChildrenSelected) {
                idsToSet.push(parent.id);
                parent = getParentNode(items, parent.id);
            } else {
                break;
            }
        }
        return idsToSet;
    }

    const getAllParentIds = (items: TreeViewBaseItem[], id: string) => {
        const parentIds: string[] = [];
        let parent = getParentNode(items, id);
        while (parent) {
            parentIds.push(parent.id);
            parent = getParentNode(items, parent.id);
        }
        return parentIds;
    }

    const getSelectedIdsAndChildrenIds = (items: TreeViewBaseItem[], selectedIds: string[],) => {
        const selectedIdIncludingChildrenIds = new Set([...selectedIds]);

        for (const item of items) {
            if (selectedIds.includes(item.id)) {
                // 現在のアイテムのIDを結果配列に追加する
                selectedIdIncludingChildrenIds.add(item.id);
                // 現在のアイテムの子に対して関数を再帰的に呼び出す
                if (item.children) {
                    const childrenIds = item.children.map((child) => child.id);
                    const childrenSelectedIds = getSelectedIdsAndChildrenIds(item.children, childrenIds,);
                    childrenSelectedIds.forEach((selectedId) => selectedIdIncludingChildrenIds.add(selectedId),);
                }
            } else if (item.children) {
                // 子供たちを散歩させて、そこにも選択肢があるかどうかを確認します
                const childrenSelectedIds = getSelectedIdsAndChildrenIds(item.children,selectedIds,);
                childrenSelectedIds.forEach((selectedId) => selectedIdIncludingChildrenIds.add(selectedId),);
            }
        }
        return [...Array.from(selectedIdIncludingChildrenIds)];
    }

    const getParentNode = (items: TreeViewBaseItem[], id: string,): TreeViewBaseItem | undefined => {
        for (const item of items) {
            if (item.children) {
                if (item.children.some((child) => child.id === id)) {
                    // 現在のアイテムは指定されたIDの親です
                    return item;
                } else {
                    // 現在のアイテムの子に対して関数を再帰的に呼び出す
                    const parentNode = getParentNode(item.children, id);
                    if (parentNode) {
                        return parentNode;
                    }
                }
            }
        }
        // 親が見つかりませんでした
        return undefined;
    }

    const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
        padding: theme.spacing(0.5, 1),
    }));
    
    interface CustomTreeItemProps extends Omit<
        UseTreeItem2Parameters, 'rootRef'>,
        Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {}
    
    const CustomTreeItem = React.forwardRef(function CustomTreeItem(
        props: CustomTreeItemProps,
        ref: React.Ref<HTMLLIElement>,
    ) {
        const { id, itemId, label, disabled, children, ...other } = props;

        const {
            getRootProps,
            getContentProps,
            getIconContainerProps,
            getCheckboxProps,
            getLabelProps,
            getGroupTransitionProps,
            getDragAndDropOverlayProps,
            status,
        } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });
    
        /**
         * 区分毎に色分け
         * @returns 
         */
        const backgroundColor = () => {
            const division = itemId.slice(-1);
            if (division === "a") {
                return "red";
            } else if (division === "b") {
                return "orange";
            } else if (division === "c") {
                return "blue";
            } else if (division === "d") {
                return "green";
            }
            return "e"; 
        }

        return (
            <TreeItem2Root {...getRootProps(other)}>
                <CustomTreeItemContent {...getContentProps()}>
                    <TreeItem2IconContainer {...getIconContainerProps()}>
                        <TreeItem2Icon status={status} />
                    </TreeItem2IconContainer>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TreeItem2Checkbox {...getCheckboxProps()} />
                            {backgroundColor() !== "e" ? 
                                <Avatar
                                    sx={(theme) => ({
                                        background: backgroundColor(),
                                        width: 18,
                                        height: 18,
                                        fontSize: '0.8rem',
                                    })}
                                >
                                    {" "}
                                </Avatar> : ""
                            }
                            <TreeItem2Label {...getLabelProps()} />
                        </Box>
                    <TreeItem2DragAndDropOverlay {...getDragAndDropOverlayProps()} />
                </CustomTreeItemContent>
                {children && <TreeItem2GroupTransition {...getGroupTransitionProps()} />}
            </TreeItem2Root>
        );
    });

    return (
        <Drawer
            className="icon-controller-wrapper"
            anchor="right"
            open={true}
            variant='persistent'
            SlideProps={{style: slidePropsStyle}}
        >
            <RichTreeView 
                checkboxSelection
                items={ALL_EVENTS}
                multiSelect={true}
                selectedItems={checkedKeys}
                onSelectedItemsChange={handleSelectedItemsChange}
                slots={{ item: CustomTreeItem }}
            />
        </Drawer>
    );
}

export default MapIconController;